/**
 * FR-018.7 — the preview → confirm → job lifecycle shared by the mapping editor and the group
 * editor (TENANT_CONFIGURATION_UI.md §5, §5.1, §6.2).
 *
 * - `useDebouncedPreview(itemsJson)`: a debounced dry run keyed on the EXACT items JSON (plus a
 *   nonce bumped by `refresh()` after a 409 `preview_stale`). Only a response for the current key
 *   counts, so Confirm can only ever send the items of the last resolved preview.
 * - `useConfirmJob()`: lives in the OUTER panel component (which stays mounted while the panel is
 *   closed), so a confirm that resolves after Esc still records its job and the `done`
 *   invalidation (`useMappingJob`) still runs. A 502 partial failure with a job id shows that job;
 *   with ANY landed id (created, superseded, retired or rejected) but no job, part of the batch is
 *   saved — Sources are re-read and Confirm stays off until a FRESH preview of the new state resolves;
 *   with nothing landed (or a 503 `sources_confirm_unavailable`) NOTHING was written: re-preview and retry.
 * - The unrestated-supersede guard (`hasUnrestatedSupersedes`) runs on EVERY confirm path: the
 *   editors use it to disable Confirm, and `submitConfirm` itself refuses a batch that trips it, so
 *   no mode (change / accept / assign / add / group) can skip it.
 */
import React from 'react';
import {
    confirmFailureDetail,
    sourcesErrorKind,
    type ConfirmFailureDetail,
    type PreviewResponse,
    type SourceMappingItem,
} from '../../../../services/sourcesService';
import { useInvalidateSources, useMappingJob, useSourceConfirm, useSourcePreview } from '../../../../hooks/useSources';
import { unexpectedSupersedes } from './supersedeGuard';

export const PREVIEW_DEBOUNCE_MS = 400;

export interface JobTarget {
    targetType: string;
    targetId: string;
    targetKind: 'entity' | 'term';
}

export interface JobState extends JobTarget {
    jobId: string | null;
}

/** A confirm that did not record a job: the error kind, and whether rows already landed. */
export interface ConfirmFailure {
    kind: string | null;
    /** Something was written (502 partial failure without a job): the spent preview must never be re-sent. */
    landed: boolean;
    /** Nothing was written and the preview must be re-run before Confirm comes back. */
    repreview?: boolean;
}

/** The client-side refusal of a batch whose preview supersedes a row it never showed. */
export const UNRESTATED_ROWS = 'unrestated_rows';
/** A confirm that wrote nothing (503, or a 502 that names no landed row and no job). */
export const CONFIRM_NOTHING_SAVED = 'confirm_nothing_saved';

/**
 * Whether the preview would supersede a live row outside `accounted` — the mapping ids the batch
 * knowingly restates or replaces (the editor's own row, the group's span rows; empty for `assign` /
 * `add` from a stub). F-16 supersedes by label, not by target, so such a row would move silently.
 */
export function hasUnrestatedSupersedes(preview: PreviewResponse, accounted: readonly string[]): boolean {
    return unexpectedSupersedes(preview, new Set(accounted)).length > 0;
}

export type SubmitConfirm = (
    body: {
        items: SourceMappingItem[];
        /** The resolved preview of exactly these items: its fingerprint is sent, its supersedes are guarded. */
        preview: PreviewResponse;
        /** The mapping ids the batch knowingly restates or replaces (see `hasUnrestatedSupersedes`). */
        accounted: readonly string[];
        note?: string;
    },
    target: JobTarget,
    onFailure: (failure: ConfirmFailure) => void,
) => void;

export function useDebouncedPreview(itemsJson: string | null, delayMs = PREVIEW_DEBOUNCE_MS) {
    const preview = useSourcePreview();
    const previewMutate = preview.mutate;
    const [nonce, setNonce] = React.useState(0);
    const [state, setState] = React.useState<{ key: string; data: PreviewResponse } | null>(null);
    const [error, setError] = React.useState<{ key: string; kind: string | null } | null>(null);
    const key = `${itemsJson ?? ''}#${nonce}`;

    React.useEffect(() => {
        if (!itemsJson) return;
        const k = `${itemsJson}#${nonce}`;
        const h = window.setTimeout(() => {
            previewMutate(JSON.parse(itemsJson) as SourceMappingItem[], {
                onSuccess: (data) => setState({ key: k, data }),
                onError: (err) => setError({ key: k, kind: sourcesErrorKind(err) }),
            });
        }, delayMs);
        return () => window.clearTimeout(h);
    }, [itemsJson, nonce, previewMutate, delayMs]);

    const current = state?.key === key ? state.data : null;
    const currentError = error?.key === key && !current ? error : null;
    const refresh = React.useCallback(() => setNonce((n) => n + 1), []);
    /** Identifies the resolved preview (items + refresh nonce), so a caller can tell a FRESH one apart. */
    const currentKey = current ? key : null;
    return { current, currentKey, error: currentError, refresh };
}

/** Whether a 502 names any id that landed — of any kind. */
function landedAnything(d: ConfirmFailureDetail): boolean {
    return [d.mapping_ids, d.superseded, d.retired, d.rejected].some((ids) => ids.length > 0);
}

export function useConfirmJob() {
    const [job, setJob] = React.useState<JobState | null>(null);
    const jobQuery = useMappingJob(job?.jobId ?? null);
    const confirm = useSourceConfirm();
    const invalidateSources = useInvalidateSources();
    const confirmMutate = confirm.mutate;
    const submitConfirm = React.useCallback<SubmitConfirm>(
        ({ items, preview, accounted, note }, target, onFailure) => {
            if (hasUnrestatedSupersedes(preview, accounted)) {
                onFailure({ kind: UNRESTATED_ROWS, landed: false });
                return;
            }
            confirmMutate(
                { items, fingerprint: preview.fingerprint, note },
                {
                    onSuccess: (res) => setJob({ jobId: res.job_id, ...target }),
                    onError: (err) => {
                        const partial = confirmFailureDetail(err);
                        const kind = sourcesErrorKind(err);
                        if (partial?.job_id) {
                            // The retry publish may have succeeded; the job's own status tells.
                            setJob({ jobId: partial.job_id, ...target });
                        } else if (partial && landedAnything(partial)) {
                            // Rows of SOME kind landed (created, superseded, retired or rejected): the
                            // preview confirm was built on is spent — Sources re-read, a fresh preview first.
                            invalidateSources();
                            onFailure({ kind: 'confirm_partial_failure', landed: true, repreview: true });
                        } else if (partial || kind === 'sources_confirm_unavailable') {
                            onFailure({ kind: CONFIRM_NOTHING_SAVED, landed: false, repreview: true });
                        } else {
                            onFailure({ kind, landed: false, repreview: kind === 'preview_stale' });
                        }
                    },
                },
            );
        },
        [confirmMutate, invalidateSources],
    );
    return { job, setJob, jobQuery, submitConfirm, submitting: confirm.isPending };
}

type T = (key: string, defaultValue: string) => string;

/** `farm.sources.errors.<kind>`, else the fallback sentence — the one error-kind lookup every panel uses. */
export function errorKindSentence(t: T, kind: string | null, fallbackKey: string, fallback: string): string {
    return t(`farm.sources.errors.${kind ?? '_'}`, t(fallbackKey, fallback));
}

/** The sentence for a failed confirm (`onFailure`), shared by both editors. */
export function confirmFailureMessage(failure: ConfirmFailure, t: T): string {
    if (failure.landed) {
        return t(
            'farm.sources.errors.confirm_partial_failure',
            'Ein Teil der Änderung wurde gespeichert; der Abschnitt Herkunft zeigt den aktuellen Stand.',
        );
    }
    if (failure.kind === 'preview_stale') {
        return t('farm.sources.warnings.preview_stale', 'Die Vorschau ist veraltet – bitte erneut prüfen.');
    }
    if (failure.kind === 'tenant_job_in_flight') {
        return t('farm.sources.warnings.tenant_job_in_flight', 'Eine andere Änderung läuft noch. Bitte in einigen Minuten erneut versuchen.');
    }
    if (failure.kind === CONFIRM_NOTHING_SAVED) {
        return t('farm.sources.errors.confirm_nothing_saved', 'Nichts wurde gespeichert – bitte erneut versuchen.');
    }
    if (failure.kind === UNRESTATED_ROWS) {
        return t(
            'farm.sources.warnings.unrestated_rows',
            'Diese Änderung würde weitere Zuordnungen desselben Namens ersetzen, die hier nicht angezeigt werden. Bitte Seite neu laden und erneut prüfen.',
        );
    }
    return errorKindSentence(t, failure.kind, 'farm.sources.errors.confirm_unavailable', 'Die Zuordnung konnte gerade nicht gespeichert werden.');
}
